import { ReactNode, forwardRef } from 'react';

import useLocalizedFilters from '../../hooks/use-localized-filters';
import _ from '../../locale';
import { styled } from '../../theme';
import { AgeGroupsOrder, AiChatFeedbackReaction, DataFilters, DataType } from '../../types';
import FlexColumn from '../FlexColumn';
import FlexRow from '../FlexRow';
import FilterChips from './FilterChips';
import {
    AssistantSideRow,
    ChipsRow,
    UserSideRow,
    assistantBubbleSurface,
    highlightBorder,
} from './styles';

const FiltersBubble = styled(FlexColumn, {
    shouldForwardProp: (prop) => prop !== 'highlightReaction' && prop !== 'isManual',
})<{ highlightReaction?: AiChatFeedbackReaction | null; isManual?: boolean }>(
    ({ theme, highlightReaction, isManual }) => ({
        ...assistantBubbleSurface(theme),
        ...(isManual && { background: theme.color.gray15 }),
        gap: 8,
        maxWidth: 360,
        width: '100%',
        border: highlightBorder(theme, highlightReaction),
    })
);

const Header = styled.div(({ theme }) => ({
    fontSize: 16,
    lineHeight: '24px',
    color: theme.color.jetBlack,
}));

const Note = styled.div(({ theme }) => ({
    fontSize: 12,
    lineHeight: 1.4,
    color: theme.color.gray70,
}));

const Actions = styled(FlexRow)({
    alignItems: 'center',
    gap: 12,
    paddingTop: 4,
    width: '100%',
});

export interface FiltersCardProps {
    dataTypes: DataType[];
    filters: DataFilters;
    ageGroupsOrder?: AgeGroupsOrder | null;
    highlightReaction?: AiChatFeedbackReaction | null;
    dataCollectionName?: string;
    actions?: ReactNode;
    note?: ReactNode;
    isManual?: boolean;
}

const FiltersCard = forwardRef<HTMLDivElement, FiltersCardProps>(
    (
        {
            dataTypes,
            filters,
            ageGroupsOrder,
            highlightReaction,
            dataCollectionName,
            actions,
            note,
            isManual = false,
        },
        ref
    ) => {
        const localizedFilters = useLocalizedFilters(dataTypes[0], filters, ageGroupsOrder);
        const Row = isManual ? UserSideRow : AssistantSideRow;

        return (
            <Row ref={ref}>
                <FiltersBubble highlightReaction={highlightReaction} isManual={isManual}>
                    <Header>
                        {isManual ? _('dex_chat_changed_manually') : _('dex_chat_applied_by_ai')}
                    </Header>
                    <ChipsRow>
                        <FilterChips
                            localizedFilters={localizedFilters}
                            dataCollectionName={dataCollectionName}
                        />
                    </ChipsRow>
                    {note && <Note>{note}</Note>}
                    {actions && <Actions>{actions}</Actions>}
                </FiltersBubble>
            </Row>
        );
    }
);

FiltersCard.displayName = 'FiltersCard';

export default FiltersCard;
